import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useCatalogoExamesConsultaControllerListar } from '@/api/generated/catalogo-exames/catalogo-exames';
import type { RequisicaoResponse } from '@/api/generated/model';
import { usePacientesControllerListar } from '@/api/generated/pacientes/pacientes';
import {
  getRequisicoesControllerListarQueryKey,
  useRequisicoesControllerAssinar,
  useRequisicoesControllerAtualizar,
  useRequisicoesControllerCriar,
} from '@/api/generated/requisicoes-exames/requisicoes-exames';
import { ApiError } from '@/api/http-client';
import { useSenhaDoCertificado } from '@/features/documentos/lib/useSenhaDoCertificado';
import { ESPECIES } from '@/features/pacientes/lib/especies';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { EstadoVazio } from '@/shared/components/estados';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { ComboBox, type OpcaoCombo } from '@/shared/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

/** Um item pendurado na requisição: ou é do catálogo, ou é texto livre. */
interface Item {
  catalogoExameId: string | null;
  nome: string;
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  requisicao?: RequisicaoResponse;
}

/**
 * ⚠️ **Item fora do catálogo é recurso, não descuido.** O `nomeLivre` existe
 * porque o veterinário precisa pedir exame que o laboratório ainda não
 * cadastrou, e travar isso empurraria o pedido para fora do sistema — que é
 * como se perde rastreabilidade. O item digitado nasce com `doCatalogo: false`
 * e não vira exame automaticamente quando o resultado volta.
 *
 * ⚠️ **Assinar aqui é o mesmo ponto sem volta da listagem.** Salvar e assinar
 * são duas chamadas (`POST` e `POST /:id/assinar`) porque o servidor só numera
 * e congela o snapshot na segunda. Se a assinatura falhar, a requisição JÁ
 * existe como rascunho — o diálogo fecha e avisa, em vez de deixar o usuário
 * apertar de novo e criar uma segunda.
 */
export function RequisicaoFormDialogo({ aberto, aoFechar, requisicao }: Props) {
  const queryClient = useQueryClient();
  const { pode } = usePermissoes();
  const criar = useRequisicoesControllerCriar();
  const atualizar = useRequisicoesControllerAtualizar();
  const assinar = useRequisicoesControllerAssinar();
  const { comSenhaSeNecessario, dialogo: dialogoDeSenha } = useSenhaDoCertificado();
  const editando = Boolean(requisicao);
  const podeAssinar = pode('requisicoes_exames.assinar');

  const [pacienteId, setPacienteId] = useState('');
  const [pacienteRotulo, setPacienteRotulo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<Item[]>([]);
  const [nomeLivre, setNomeLivre] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [confirmandoAssinatura, setConfirmandoAssinatura] = useState(false);

  const [buscaPaciente, setBuscaPaciente] = useState('');
  const [buscaExame, setBuscaExame] = useState('');
  const pacienteAdiado = useDebounce(buscaPaciente);
  const exameAdiado = useDebounce(buscaExame);

  // A busca vai ao servidor porque a página é de 100: com o catálogo inteiro
  // (centenas de exames) filtrar só o que veio deixaria itens inalcançáveis.
  const { data: pacientes, isFetching: buscandoPacientes } = usePacientesControllerListar(
    {
      pagina: 1,
      porPagina: 100,
      ordenarPor: 'nome',
      ordem: 'asc',
      ...(pacienteAdiado.trim() ? { busca: pacienteAdiado.trim() } : {}),
    },
    { query: { enabled: aberto && !editando } },
  );

  const { data: catalogo, isFetching: buscandoExames } = useCatalogoExamesConsultaControllerListar(
    {
      pagina: 1,
      porPagina: 100,
      ordenarPor: 'nome',
      ordem: 'asc',
      ...(exameAdiado.trim() ? { busca: exameAdiado.trim() } : {}),
    },
    { query: { enabled: aberto } },
  );

  const opcoesPacientes: OpcaoCombo[] = useMemo(
    () =>
      pacientes?.itens.map((p) => ({
        valor: p.id,
        rotulo: p.nome,
        // Homônimo é a regra, não a exceção ("NINO" três vezes). Sem o tutor e a
        // espécie a escolha vira sorteio.
        detalhe: [ESPECIES[p.especie], p.tutorNome].filter(Boolean).join(' · '),
      })) ?? [],
    [pacientes],
  );

  const opcoesExames: OpcaoCombo[] = useMemo(
    () =>
      catalogo?.itens.map((c) => ({
        valor: c.id,
        rotulo: c.nome,
        ...(c.categoria ? { detalhe: c.categoria } : {}),
        // Repetir o mesmo exame é sempre engano de clique: aparece marcado.
        desabilitada: itens.some((i) => i.catalogoExameId === c.id),
      })) ?? [],
    [catalogo, itens],
  );

  useEffect(() => {
    if (!aberto) return;

    setPacienteId(requisicao?.pacienteId ?? '');
    setPacienteRotulo(requisicao?.pacienteNome ?? '');
    setObservacoes(requisicao?.observacoes ?? '');
    setItens(
      requisicao?.itens.map((i) => ({ catalogoExameId: i.catalogoExameId, nome: i.nome })) ?? [],
    );
    setNomeLivre('');
    setErro(null);
    setConfirmandoAssinatura(false);
    setBuscaPaciente('');
    setBuscaExame('');
  }, [aberto, requisicao]);

  function acrescentarDoCatalogo(id: string) {
    const exame = catalogo?.itens.find((c) => c.id === id);
    if (!exame) return;

    setItens((atual) => [...atual, { catalogoExameId: id, nome: exame.nome }]);
    setBuscaExame('');
  }

  function acrescentarLivre() {
    const nome = nomeLivre.trim();
    if (nome.length < 2) {
      toast.error('Informe ao menos 2 caracteres.');
      return;
    }

    setItens((atual) => [...atual, { catalogoExameId: null, nome }]);
    setNomeLivre('');
  }

  /** Devolve a queixa, ou `null` se está pronto para ir ao servidor. */
  function validar(): string | null {
    if (!editando && !pacienteId) return 'Escolha o paciente.';
    if (itens.length === 0) return 'Acrescente ao menos um exame.';
    return null;
  }

  function pedirAssinatura() {
    const queixa = validar();
    if (queixa) {
      setErro(queixa);
      return;
    }
    setErro(null);
    setConfirmandoAssinatura(true);
  }

  async function salvar(assinando: boolean) {
    const queixa = validar();
    if (queixa) {
      setErro(queixa);
      setConfirmandoAssinatura(false);
      return;
    }

    setErro(null);

    const corpoItens = itens.map((i) =>
      i.catalogoExameId ? { catalogoExameId: i.catalogoExameId } : { nomeLivre: i.nome },
    );
    const obs = observacoes.trim() ? observacoes.trim() : null;

    setEnviando(true);
    try {
      const salva = requisicao
        ? await atualizar.mutateAsync({
            id: requisicao.id,
            data: { observacoes: obs, itens: corpoItens },
          })
        : await criar.mutateAsync({ data: { pacienteId, observacoes: obs, itens: corpoItens } });

      if (!assinando) {
        toast.success(editando ? 'Requisição atualizada.' : 'Requisição salva como rascunho.');
      } else {
        try {
          const assinada = await comSenhaSeNecessario((senhaCertificado) =>
            assinar.mutateAsync({
              id: salva.id,
              ...(senhaCertificado ? { data: { senhaCertificado } } : {}),
            }),
          );

          if (assinada) {
            toast.success(
              assinada.assinaturaDigital
                ? 'Requisição assinada digitalmente.'
                : 'Requisição assinada.',
            );
          } else {
            // Cancelou a senha do certificado: a requisição já foi salva.
            toast.info('Requisição salva como rascunho. Dá para assinar depois, pela listagem.');
          }
        } catch (e) {
          // O documento não se perdeu: ficou rascunho. Dizer isso evita que o
          // usuário refaça o formulário e acabe com duas requisições.
          toast.warning(
            e instanceof ApiError
              ? `Salva como rascunho, mas não foi possível assinar: ${e.message}`
              : 'Salva como rascunho, mas não foi possível assinar.',
          );
        }
      }

      await queryClient.invalidateQueries({ queryKey: getRequisicoesControllerListarQueryKey() });
      aoFechar();
    } catch (e) {
      setConfirmandoAssinatura(false);
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar a requisição.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar requisição' : 'Nova requisição'}</DialogTitle>
            <DialogDescription>
              Escolha os exames do portfólio ou digite um que ainda não esteja lá.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {erro && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {erro}
              </div>
            )}

            {editando ? (
              <div className="rounded-lg bg-muted/50 px-4 py-3 flex justify-between text-sm">
                <span className="text-muted-foreground">Paciente</span>
                <span className="font-medium text-foreground">{requisicao?.pacienteNome}</span>
              </div>
            ) : (
              <Campo id="pacienteId" rotulo="Paciente" obrigatorio>
                <ComboBox
                  id="pacienteId"
                  aria-label="Paciente"
                  className={CLASSE_CONTROLE}
                  valor={pacienteId}
                  rotuloSelecionado={pacienteRotulo}
                  opcoes={opcoesPacientes}
                  aoEscolher={(id) => {
                    setPacienteId(id);
                    setPacienteRotulo(opcoesPacientes.find((o) => o.valor === id)?.rotulo ?? '');
                  }}
                  aoBuscar={setBuscaPaciente}
                  carregando={buscandoPacientes}
                  placeholder="Selecione"
                  placeholderBusca="Buscar por nome do paciente..."
                  vazio="Nenhum paciente encontrado."
                />
              </Campo>
            )}

            <div className="space-y-3 rounded-lg border border-brand/20 p-4">
              <h4 className="text-sm font-semibold text-brand uppercase tracking-wide">Exames</h4>

              <Campo id="doCatalogo" rotulo="Do portfólio">
                <ComboBox
                  id="doCatalogo"
                  aria-label="Acrescentar exame do portfólio"
                  className={CLASSE_CONTROLE}
                  valor=""
                  opcoes={opcoesExames}
                  aoEscolher={acrescentarDoCatalogo}
                  aoBuscar={setBuscaExame}
                  carregando={buscandoExames}
                  placeholder="Escolha para acrescentar"
                  placeholderBusca="Buscar exame pelo nome..."
                  vazio="Nenhum exame encontrado no portfólio."
                />
              </Campo>

              <Campo id="nomeLivre" rotulo="Outros exames" dica="Para exame ainda não cadastrado.">
                <div className="flex gap-2">
                  <Input
                    id="nomeLivre"
                    value={nomeLivre}
                    onChange={(e) => setNomeLivre(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        acrescentarLivre();
                      }
                    }}
                    placeholder="Nome do exame"
                    className={CLASSE_CONTROLE}
                  />
                  <Button type="button" variant="outline" onClick={acrescentarLivre}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </Campo>

              {itens.length === 0 ? (
                <EstadoVazio titulo="Nenhum exame na requisição" className="py-6" />
              ) : (
                <ul className="space-y-2">
                  {itens.map((item, indice) => (
                    <li
                      key={`${item.catalogoExameId ?? 'livre'}-${item.nome}-${indice}`}
                      className="flex items-start justify-between gap-3 rounded-md bg-muted/40 px-3 py-2"
                    >
                      <span className="min-w-0 flex-1 break-words text-sm text-foreground">
                        {item.nome}
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        {!item.catalogoExameId && <Badge variant="outline">outros exames</Badge>}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remover ${item.nome}`}
                          onClick={() => setItens((atual) => atual.filter((_, i) => i !== indice))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Campo id="observacoes" rotulo="Observações">
              <Textarea
                id="observacoes"
                rows={3}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                className={CLASSE_CONTROLE}
              />
            </Campo>

            {confirmandoAssinatura && (
              <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Ao assinar, a requisição recebe número e não poderá mais ser editada nem excluída.
                  É o que permite compartilhar o documento.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            {confirmandoAssinatura ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmandoAssinatura(false)}
                  disabled={enviando}
                >
                  Voltar
                </Button>
                <Button onClick={() => void salvar(true)} disabled={enviando}>
                  {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirmar e assinar
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant={podeAssinar ? 'outline' : 'default'}
                  onClick={() => void salvar(false)}
                  disabled={enviando}
                >
                  {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar rascunho
                </Button>
                {podeAssinar && (
                  <Button type="button" onClick={pedirAssinatura} disabled={enviando}>
                    {editando ? 'Salvar e assinar' : 'Criar e assinar'}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {dialogoDeSenha}
    </>
  );
}
